import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.headerComponent?.addEventListener('pointerdown', this.#onHeaderPointerDownCapture, true);
    this.addEventListener('pointerenter', this.#onMenuPointerEnterCapture, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.headerComponent?.removeEventListener('pointerdown', this.#onHeaderPointerDownCapture, true);
    this.removeEventListener('pointerenter', this.#onMenuPointerEnterCapture, true);
    this.#clearPointerLeaveCloseTimer();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close an open mega menu when the user presses outside this component (e.g. logo / search),
   * so hover/focus state cannot leave the wrong top-level item looking active.
   * @param {PointerEvent} event
   */
  #onHeaderPointerDownCapture = (event) => {
    if (!this.#state.activeItem) return;
    // Use composedPath so clicks inside <overflow-list> shadow parts still count as "inside" this menu.
    if (event.composedPath().includes(this)) return;
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Delayed close after pointerleave on a row item (bridge over the gap to the mega panel).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerLeaveCloseTimer;

  #clearPointerLeaveCloseTimer() {
    if (this.#pointerLeaveCloseTimer != null) {
      clearTimeout(this.#pointerLeaveCloseTimer);
      this.#pointerLeaveCloseTimer = undefined;
    }
  }

  #onMenuPointerEnterCapture = () => {
    this.#clearPointerLeaveCloseTimer();
  };

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    if (!this.#isPointerInsideMenuHoverArea(event.clientX, event.clientY, activeLink)) {
      this.#deactivate(activeLink, { force: true });
      return;
    }

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Keep desktop mega menus open only over the active top-level item, the menu content,
   * and a small bridge between them.
   * @param {number} x
   * @param {number} y
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isPointerInsideMenuHoverArea(x, y, item) {
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return true;

    // Overflow ("More") menu: `item` is the first overflowed link, slotted low inside the
    // panel, so the item→content bridge below never spans the header→panel gap. Bridge from
    // the More button in the nav row down to the overflow panel instead.
    if (this.dataset.overflowExpanded === 'true') {
      const gap = 100;
      const moreRect = this.querySelector('[slot="more"]')?.getBoundingClientRect();
      const panelRect = this.overflowMenu?.getBoundingClientRect();

      if (moreRect && isPointInRect(x, y, moreRect, gap)) return true;
      if (panelRect && isPointInRect(x, y, panelRect, gap)) return true;

      if (moreRect && panelRect) {
        const bridge = {
          left: Math.min(moreRect.left, panelRect.left) - gap,
          right: Math.max(moreRect.right, panelRect.right) + gap,
          top: Math.min(moreRect.bottom, panelRect.top) - gap,
          bottom: Math.max(moreRect.bottom, panelRect.top) + gap,
        };
        if (isPointInRect(x, y, bridge, 0)) return true;
      }

      return false;
    }

    const submenu = findSubmenu(item);
    // Measure the panel wrapper rather than the inner grid: the Shop grid is capped at 80rem and
    // centred, so its rect stops short of the panel's real edges on wide viewports.
    const menuContent =
      submenu?.querySelector('.menu-list__submenu-inner') ?? submenu?.querySelector('.mega-menu__grid') ?? submenu;
    const gap = 100;
    // The panel's bottom edge is a hard boundary. A 100px slack there kept tall panels (Shop fills
    // most of the viewport) open over the page content directly beneath them, so the pointer had to
    // travel a long way past the visible menu before it closed.
    const bottomGap = 12;
    const itemRect = item.getBoundingClientRect();
    const contentRect = menuContent?.getBoundingClientRect();

    if (contentRect && y > contentRect.bottom + bottomGap) return false;
    if (isPointInRect(x, y, itemRect, gap)) return true;
    if (contentRect && isPointInRect(x, y, contentRect, gap)) return true;

    if (contentRect) {
      const bridge = {
        left: Math.min(itemRect.left, contentRect.left) - gap,
        right: Math.max(itemRect.right, contentRect.right) + gap,
        top: Math.min(itemRect.bottom, contentRect.top) - gap,
        bottom: Math.max(itemRect.bottom, contentRect.top) + gap,
      };

      if (isPointInRect(x, y, bridge, 0)) return true;
    }

    return false;
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#clearPointerLeaveCloseTimer();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerleave') {
      const openerItem = this.#state.activeItem;
      if (!openerItem) return;

      const leaveX = event instanceof PointerEvent ? event.clientX : this.#lastPointer.x;
      const leaveY = event instanceof PointerEvent ? event.clientY : this.#lastPointer.y;

      this.#clearPointerLeaveCloseTimer();
      this.#pointerLeaveCloseTimer = setTimeout(() => {
        this.#pointerLeaveCloseTimer = undefined;
        if (!this.#state.activeItem || this.#state.activeItem !== openerItem) return;
        if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

        const submenu = findSubmenu(openerItem);
        /** @type {[number, number][]} */
        const probePoints = [
          [this.#lastPointer.x, this.#lastPointer.y],
          [leaveX, leaveY],
        ];

        for (const [x, y] of probePoints) {
          const probe = document.elementFromPoint(x, y);
          if (!probe) continue;

          if (isNodeInsideFlyout(probe, openerItem, submenu)) return;
        }

        // Still within the hover bridge (e.g. crossing the gap to the "More" panel)? Keep open.
        if (this.#isPointerInsideMenuHoverArea(this.#lastPointer.x, this.#lastPointer.y, openerItem)) return;

        this.#deactivate();
      }, 90);
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    this.#clearPointerLeaveCloseTimer();
    if (!item || item != this.#state.activeItem) return;

    const force = options.force === true;
    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const selfOrAncestor = element.closest('[ref="menuitem"]');
  if (selfOrAncestor instanceof HTMLElement) return selfOrAncestor;

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {Node} node
 * @param {HTMLElement} openerItem
 * @param {HTMLElement | null} submenu
 * @returns {boolean}
 */
function isNodeInsideFlyout(node, openerItem, submenu) {
  return openerItem.contains(node) || Boolean(submenu?.contains(node));
}

/**
 * @param {number} x
 * @param {number} y
 * @param {{ left: number, right: number, top: number, bottom: number }} rect
 * @param {number} inset
 * @returns {boolean}
 */
function isPointInRect(x, y, rect, inset) {
  return x >= rect.left - inset && x <= rect.right + inset && y >= rect.top - inset && y <= rect.bottom + inset;
}
